import React, { useState, useMemo } from 'react';
import talentsData from '../../../data/talents.json';

interface TalentsTabProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedRulebookIds: string[];
}

export const TalentsTab: React.FC<TalentsTabProps> = ({
  searchTerm,
  setSearchTerm,
  selectedRulebookIds
}) => {
  interface Talent {
    id: string;
    name: string;
    tier: number;
    prerequisites: string[];
    shortDescription: string;
    fullDescription: string;
    rulebook: string;
    isSpecialized: boolean;
  }

  const talents = talentsData.talents as Talent[];
  const [selectedTier, setSelectedTier] = useState<string>('all');

  const visibleTalents = useMemo(
    () => talents.filter((talent) => selectedRulebookIds.includes(talent.rulebook)),
    [talents, selectedRulebookIds]
  );

  const filteredTalents = useMemo(() => {
    let filtered = visibleTalents;

    if (selectedTier !== 'all') {
      filtered = filtered.filter((t) => t.tier === parseInt(selectedTier));
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (talent) =>
          talent.name.toLowerCase().includes(term) ||
          talent.shortDescription.toLowerCase().includes(term) ||
          talent.fullDescription.toLowerCase().includes(term) ||
          talent.prerequisites.some((p) => p.toLowerCase().includes(term))
      );
    }

    return filtered;
  }, [searchTerm, selectedTier, visibleTalents]);

  const getTierColor = (tier: number): string => {
    const colors = [
      'bg-surface-hover text-ink',
      'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
      'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200',
      'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200',
      'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200'
    ];
    return colors[tier] || colors[0];
  };

  return (
    <div>
      {/* Filters */}
      <div className="bg-surface-alt rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Search Talents</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, description, or prerequisites..."
              className="w-full px-3 py-2 border border-line rounded-md bg-surface text-ink focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Tier</label>
            <select
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value)}
              className="w-full px-3 py-2 border border-line rounded-md bg-surface text-ink focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Tiers</option>
              <option value="1">Tier 1</option>
              <option value="2">Tier 2</option>
              <option value="3">Tier 3</option>
            </select>
          </div>
        </div>
        <div className="mt-2 text-sm text-ink-dim">
          Showing {filteredTalents.length} of {visibleTalents.length} talents
        </div>
      </div>

      {/* Talents List */}
      <div className="space-y-3">
        {filteredTalents.map((talent) => (
          <div
            key={talent.id}
            className="group bg-surface border border-line rounded-lg p-4 hover:shadow-lg transition-shadow"
            title={talent.fullDescription}
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-lg font-semibold text-ink">{talent.name}</h3>
              <div className="flex gap-2">
                <span className={`px-2 py-1 text-xs rounded ${getTierColor(talent.tier)}`}>
                  Tier {talent.tier}
                </span>
                {talent.isSpecialized && (
                  <span className="px-2 py-1 text-xs rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200">
                    Specialized
                  </span>
                )}
              </div>
            </div>

            {/* Prerequisites */}
            {talent.prerequisites && talent.prerequisites.length > 0 && (
              <div className="mb-2">
                <span className="text-xs font-medium text-ink-dim">Prerequisites: </span>
                <span className="text-xs text-ink">{talent.prerequisites.join(', ')}</span>
              </div>
            )}

            {/* Short Description */}
            <p className="text-ink text-sm mb-2">{talent.shortDescription}</p>

            {/* Full Description */}
            {talent.fullDescription !== talent.shortDescription && (
              <details className="mt-2">
                <summary className="text-sm text-blue-600 dark:text-blue-400 cursor-pointer hover:underline">
                  Show full description
                </summary>
                <p className="mt-2 text-sm text-ink-dim pl-4 border-l-2 border-line">
                  {talent.fullDescription}
                </p>
              </details>
            )}
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredTalents.length === 0 && (
        <div className="text-center py-12">
          <p className="text-ink-faint text-lg">No talents found matching your criteria</p>
        </div>
      )}
    </div>
  );
};
